import Numeral, { NumeralJSLocale } from "numeral"
import React, { useCallback } from "react"

import { L10n } from "@encoway/l10n"

/**
 * Convert the argument string representation of a number to a string representation of the same number for the argument locale.
 * Conversion is done by replacing the delimiters by the ones used for the given locale.
 * If the conversion does not produce a valid string representation within the given locale, the origin string is returned.
 * @param input to be converted.
 * @param displayLocale to specify locale that specifies the delimiters to use converting (given as full locale).
 * @returns the input argument using the delimiters specified by the argument locale
 */
export const convertNumberString = (input: string, displayLocale: string) => {
    const def: NumeralJSLocale = registerNumeralForLocale(displayLocale)
    const converted = input
        .replace(new RegExp("\\" + def.delimiters.thousands, "g"), "@")
        .replace(
            new RegExp("\\" + def.delimiters.decimal, "g"),
            def.delimiters.thousands
        )
        .replace(/@/g, def.delimiters.decimal)
    if (!isValidInput(converted, def.delimiters)) {
        console.error(
            "Conversion('" +
                converted +
                "') of argument '" +
                input +
                "' is no valid representation of a number, returning argument..."
        )
        return input
    }
    return converted
}

/**
 * Validates if a given string is a correctly formatted number.
 * If the range argument is provided it also checks whether the number is in the range.
 *
 * @param {string} input - The string that will be validated.
 * @param {string} fullLocale - Full locale to use for converting the argument input to a number
 * @param {number|string} minValue - Optional allowed minimal value to check the input against.
 * @param {number|string} maxValue - Optional allowed maximal value to check the input against.
 * @returns {number|NaN} The input as number if valid, NaN otherwise.
 *
 */
export const validateNumericInput: (
    input: string,
    fullLocale: string,
    minValue?: number | string,
    maxValue?: number | string
) => number = (input, fullLocale, minValue, maxValue) => {
    const def: NumeralJSLocale = registerNumeralForLocale(fullLocale)

    const number = Numeral(input)?.value()

    if (
        number === null ||
        isNaN(number) ||
        !isValidInput(input, def.delimiters)
    ) {
        return NaN
    }
    if (
        (minValue !== undefined && number < minValue) ||
        (maxValue !== undefined && number > maxValue)
    ) {
        return NaN
    }
    return number
}
interface IDelimiters {
    thousands: string
    decimal: string
}
const isValidInput = (input: string, delimiters: IDelimiters) => {
    const regExp = new RegExp(
        `^[+-]?(\\d{1,2}[${delimiters.thousands}])?((\\d{3}[${delimiters.thousands}]|\\d{4,}}))*?\\d+([${delimiters.decimal}]\\d+)?$`
    )
    return regExp.test(input)
}

const registerNumeralForLocale = (fullLocale: string) => {
    const parts = new Intl.NumberFormat(fullLocale).formatToParts(12345.6)
    const grouping = parts.find((d) => d.type === "group")?.value
    const decimal = parts.find((d) => d.type === "decimal")?.value

    let locale = fullLocale.toLowerCase()
    if (locale === "de-de") {
        locale = `${locale}-cui`
    }

    try {
        if (!Numeral.locales[locale]) {
            const registerArgs = {
                delimiters: {
                    thousands: grouping,
                    decimal
                },
                abbreviations: {},
                currency: {}
            }
            Numeral.register("locale", locale, registerArgs as NumeralJSLocale)
        }
    } catch (e) {
        console.error(e)
    }
    Numeral.locale(locale)

    return Numeral.locales[locale]
}

type ValidateOptions = {
    updateError?: boolean
    removeError?: boolean
}

export const useValidation = (
    component: string,
    setError: React.Dispatch<React.SetStateAction<string | undefined>>,
    formattedMinValue?: string,
    formattedMaxValue?: string,
    minValue?: number | string,
    maxValue?: number | string
) => {
    return useCallback(
        (currentValue: string, options?: ValidateOptions) => {
            let validatedNumber = NaN
            if (currentValue !== "") {
                validatedNumber = validateNumericInput(
                    currentValue,
                    L10n.currentFullLocale(),
                    minValue,
                    maxValue
                )
            }
            if (
                options?.updateError &&
                isNaN(validatedNumber) &&
                currentValue !== formattedMaxValue &&
                currentValue !== formattedMinValue
            ) {
                if (
                    (!maxValue || maxValue === "Infinity") &&
                    (!minValue || minValue === "-Infinity")
                ) {
                    setError(
                        L10n.format(
                            `Configuration.${component}.input.invalid`,
                            {
                                input: currentValue
                            }
                        )
                    )
                } else {
                    setError(
                        L10n.format(
                            `Configuration.${component}.input.outofbounds`,
                            {
                                originalMinValue:
                                    formattedMinValue ??
                                    `${L10n.format(
                                        "Configuration.Quantity.Negative.Infinity"
                                    )}`,
                                originalMaxValue:
                                    formattedMaxValue ??
                                    `${L10n.format(
                                        "Configuration.Quantity.Positive.Infinity"
                                    )}`
                            }
                        )
                    )
                }
            } else if (options?.removeError) {
                setError(undefined)
            }
            return validatedNumber
        },
        [
            component,
            minValue,
            maxValue,
            formattedMinValue,
            formattedMaxValue,
            setError,
            L10n
        ]
    )
}
